import { useState } from 'react';

import { Button } from '../02-atoms/Button';

export interface SliderNavigationProps {
  /** Pages */
  pages: (string | undefined)[];
  /** Change page handler */
  onChange?: (tab: number) => void;
  /** Show swipe indicator on mobile */
  swipe?: boolean;
  /** Current page */
  page?: number | null;
  /** Additional class */
  className?: string;
  /** Optional label for previous page */
  prevLabel?: string;
  /** Optional label for next page */
  nextLabel?: string;
  /** Optional label for swipe indicator */
  swipeLabel?: string;
}

/** A slider navigation */
export const SliderNavigation = ({
  pages,
  onChange,
  swipe,
  page = null,
  className,
  prevLabel = 'Previous page',
  nextLabel = 'Next page',
  swipeLabel = 'Swipe to navigate',
}: SliderNavigationProps) => {
  const [currentPage, setPage] = useState<number>(page ?? 0);
  const [prevPageProp, setPrevPageProp] = useState<number | null>(page);

  const clampIndex = (index: number) =>
    Math.max(0, Math.min(pages.length - 1, index));

  const slideTo = (index: number | 'next' | 'prev') => {
    let nextIndex: number;

    if (index === 'next') {
      nextIndex = clampIndex((currentPage ?? 0) + 1);
    } else if (index === 'prev') {
      nextIndex = clampIndex((currentPage ?? 0) - 1);
    } else {
      nextIndex = clampIndex(index);
    }

    setPage(nextIndex);
    onChange?.(nextIndex);
  };

  // Sync from the controlled prop without notifying onChange: a prop sync is
  // not a user action, and notifying the parent during render both crashed
  // ("Too many re-renders") and reset the slide index the parent had just
  // set — e.g. after adding a slide in the slider block sidebar (#1536).
  if (page !== prevPageProp) {
    setPrevPageProp(page);
    if (page !== null && page !== currentPage) {
      setPage(clampIndex(page));
    }
  }

  return (
    <>
      {pages.length > 1 && (
        <div className={['slider-navigation', className].join(' ')}>
          <Button
            title={prevLabel}
            className={[
              'slider-navigation__prev',
              currentPage === 0 ? 'slider-navigation--hidden' : '',
            ].join(' ')}
            type="primary"
            icon="arrow_left_triangle"
            onClick={(e) => {
              e.preventDefault();
              slideTo('prev');
            }}
          />
          {pages.map((page, i) => (
            <div
              key={i}
              className={[
                'slider-navigation__button',
                currentPage === i ? 'slider-navigation__button--active' : '',
              ].join(' ')}
            >
              {page}
            </div>
          ))}
          <Button
            title={nextLabel}
            className={[
              'slider-navigation__next',
              currentPage === pages.length - 1
                ? 'slider-navigation--hidden'
                : '',
            ].join(' ')}
            type="primary"
            icon="arrow_right_triangle"
            onClick={(e) => {
              e.preventDefault();
              slideTo('next');
            }}
          />
          {swipe && (
            <Button
              title={swipeLabel}
              className="slider-navigation__swipe"
              type="primary"
              icon="drag_left"
            />
          )}
        </div>
      )}
    </>
  );
};
